import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  ListItemButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import {
  X,
  Settings,
  Bell,
  Eye,
  Archive,
  Lock,
  Globe,
  Users,
  Mail,
  ChevronDown,
  Save,
  AlertTriangle,
  Shield,
  Calendar,
  Trash2,
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Project, ProjectStatus } from '@/types';
import { projectService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';

interface ProjectSettingsModalProps {
  open: boolean;
  onClose: () => void;
  project: Project;
  onArchive?: () => void;
  onDelete?: () => void;
}

interface ProjectSettings {
  // Visibility
  visibility: 'private' | 'team' | 'organization';
  allowExternalAccess: boolean;
  
  // Notifications
  emailNotifications: boolean;
  taskAssignmentNotifications: boolean;
  milestoneNotifications: boolean;
  dailyDigest: boolean;
  weeklyReport: boolean;
  
  // Access Control
  requireApprovalForTasks: boolean;
  allowMemberInvites: boolean;
  lockCompletedTasks: boolean;
  
  // Automation
  autoArchiveOnComplete: boolean;
  autoAssignOwnerToTasks: boolean;
}

const defaultSettings: ProjectSettings = {
  visibility: 'team',
  allowExternalAccess: false,
  emailNotifications: true,
  taskAssignmentNotifications: true,
  milestoneNotifications: true,
  dailyDigest: false,
  weeklyReport: true,
  requireApprovalForTasks: false,
  allowMemberInvites: true,
  lockCompletedTasks: false,
  autoArchiveOnComplete: false,
  autoAssignOwnerToTasks: true,
};

/**
 * ProjectSettingsModal
 * Modal for configuring project settings, visibility, and notifications
 */
const ProjectSettingsModal: React.FC<ProjectSettingsModalProps> = ({
  open,
  onClose,
  project,
  onArchive,
  onDelete,
}) => {
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState<ProjectSettings>(defaultSettings);
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | false>('visibility');

  // Reset settings when modal opens
  useEffect(() => {
    if (open) {
      // In a real app, load settings from project or API
      setSettings(defaultSettings);
      setHasChanges(false);
    }
  }, [open, project]);

  // Update settings mutation
  const updateMutation = useMutation({
    mutationFn: async (newSettings: ProjectSettings) => {
      // In a real app, this would call an API
      // For now, we'll update the project with a settings field
      return projectService.updateProject(project.id, {
        // Settings would be stored as part of project metadata
      } as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.project(project.id) });
      setHasChanges(false);
    },
  });

  // Archive project mutation
  const archiveMutation = useMutation({
    mutationFn: async () => {
      return projectService.updateProject(project.id, {
        status: ProjectStatus.CANCELLED, // Using CANCELLED as archive state
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.project(project.id) });
      onArchive?.();
      onClose();
    },
  });

  const handleSettingChange = <K extends keyof ProjectSettings>(
    key: K,
    value: ProjectSettings[K]
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateMutation.mutate(settings);
  };

  const handleArchive = () => {
    if (window.confirm('Are you sure you want to archive this project? It will be marked as cancelled.')) {
      archiveMutation.mutate();
    }
  };

  const getVisibilityIcon = (visibility: ProjectSettings['visibility']) => {
    switch (visibility) {
      case 'private':
        return <Lock size={18} />;
      case 'team':
        return <Users size={18} />;
      case 'organization':
        return <Globe size={18} />;
    }
  };

  const isArchived = project.status === ProjectStatus.CANCELLED;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Settings size={20} />
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Project Settings
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {project.name}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Archived Warning */}
        {isArchived && (
          <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
            This project is archived. Some settings may be read-only.
          </Alert>
        )}

        {/* Visibility Section */}
        <Accordion
          expanded={expandedSection === 'visibility'}
          onChange={(_, expanded) => setExpandedSection(expanded ? 'visibility' : false)}
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ChevronDown size={18} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Eye size={18} />
              <Box>
                <Typography variant="subtitle2">Visibility & Access</Typography>
                <Typography variant="caption" color="text.secondary">
                  Control who can view and access this project
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <List dense disablePadding>
              {/* Visibility Options */}
              {(['private', 'team', 'organization'] as const).map((visibility) => (
                <ListItemButton
                  key={visibility}
                  onClick={() => handleSettingChange('visibility', visibility)}
                  selected={settings.visibility === visibility}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    border: '1px solid',
                    borderColor: settings.visibility === visibility ? 'primary.main' : 'transparent',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getVisibilityIcon(visibility)}
                  </ListItemIcon>
                  <ListItemText
                    primary={visibility.charAt(0).toUpperCase() + visibility.slice(1)}
                    secondary={
                      visibility === 'private'
                        ? 'Only you and invited members'
                        : visibility === 'team'
                        ? 'All team members can view'
                        : 'Everyone in the organization'
                    }
                  />
                  {settings.visibility === visibility && (
                    <Chip size="small" label="Active" color="primary" />
                  )}
                </ListItemButton>
              ))}

              <Divider sx={{ my: 1.5 }} />

              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Globe size={18} />
                </ListItemIcon>
                <ListItemText
                  primary="Allow External Access"
                  secondary="Enable access for external collaborators"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={settings.allowExternalAccess}
                    onChange={(e) => handleSettingChange('allowExternalAccess', e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Notifications Section */}
        <Accordion
          expanded={expandedSection === 'notifications'}
          onChange={(_, expanded) => setExpandedSection(expanded ? 'notifications' : false)}
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ChevronDown size={18} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Bell size={18} />
              <Box>
                <Typography variant="subtitle2">Notifications</Typography>
                <Typography variant="caption" color="text.secondary">
                  Configure email and in-app alerts
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <List dense disablePadding>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Mail size={18} />
                </ListItemIcon>
                <ListItemText
                  primary="Email Notifications"
                  secondary="Receive updates via email"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={settings.emailNotifications}
                    onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>

              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Users size={18} />
                </ListItemIcon>
                <ListItemText
                  primary="Task Assignments"
                  secondary="Notify when tasks are assigned"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={settings.taskAssignmentNotifications}
                    onChange={(e) => handleSettingChange('taskAssignmentNotifications', e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>

              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Calendar size={18} />
                </ListItemIcon>
                <ListItemText
                  primary="Milestone Alerts"
                  secondary="Notify on milestone due dates"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={settings.milestoneNotifications}
                    onChange={(e) => handleSettingChange('milestoneNotifications', e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>

              <Divider sx={{ my: 1.5 }} />

              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Mail size={18} />
                </ListItemIcon>
                <ListItemText
                  primary="Daily Digest"
                  secondary="Summary of daily activity"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={settings.dailyDigest}
                    onChange={(e) => handleSettingChange('dailyDigest', e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>

              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Calendar size={18} />
                </ListItemIcon>
                <ListItemText
                  primary="Weekly Report"
                  secondary="Progress report every Monday"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={settings.weeklyReport}
                    onChange={(e) => handleSettingChange('weeklyReport', e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Access Control Section */}
        <Accordion
          expanded={expandedSection === 'access'}
          onChange={(_, expanded) => setExpandedSection(expanded ? 'access' : false)}
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ChevronDown size={18} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Shield size={18} />
              <Box>
                <Typography variant="subtitle2">Access Control</Typography>
                <Typography variant="caption" color="text.secondary">
                  Manage permissions and task rules
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <List dense disablePadding>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Lock size={18} />
                </ListItemIcon>
                <ListItemText
                  primary="Require Task Approval"
                  secondary="Tasks need approval before starting"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={settings.requireApprovalForTasks}
                    onChange={(e) => handleSettingChange('requireApprovalForTasks', e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>

              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Users size={18} />
                </ListItemIcon>
                <ListItemText
                  primary="Allow Member Invites"
                  secondary="Members can invite others"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={settings.allowMemberInvites}
                    onChange={(e) => handleSettingChange('allowMemberInvites', e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>

              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Lock size={18} />
                </ListItemIcon>
                <ListItemText
                  primary="Lock Completed Tasks"
                  secondary="Prevent editing completed tasks"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={settings.lockCompletedTasks}
                    onChange={(e) => handleSettingChange('lockCompletedTasks', e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Automation Section */}
        <Accordion
          expanded={expandedSection === 'automation'}
          onChange={(_, expanded) => setExpandedSection(expanded ? 'automation' : false)}
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ChevronDown size={18} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Settings size={18} />
              <Box>
                <Typography variant="subtitle2">Automation</Typography>
                <Typography variant="caption" color="text.secondary">
                  Automatic actions and workflows
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <List dense disablePadding>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Archive size={18} />
                </ListItemIcon>
                <ListItemText
                  primary="Auto-Archive on Complete"
                  secondary="Archive project when all tasks done"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={settings.autoArchiveOnComplete}
                    onChange={(e) => handleSettingChange('autoArchiveOnComplete', e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>

              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Users size={18} />
                </ListItemIcon>
                <ListItemText
                  primary="Auto-Assign Owner"
                  secondary="Automatically assign project owner to new tasks"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={settings.autoAssignOwnerToTasks}
                    onChange={(e) => handleSettingChange('autoAssignOwnerToTasks', e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Danger Zone */}
        <Accordion
          expanded={expandedSection === 'danger'}
          onChange={(_, expanded) => setExpandedSection(expanded ? 'danger' : false)}
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ChevronDown size={18} />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <AlertTriangle size={18} color="#dc2626" />
              <Box>
                <Typography variant="subtitle2" color="error.main">
                  Danger Zone
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Irreversible actions
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Archive Project */}
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'warning.main',
                  borderRadius: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography variant="subtitle2">Archive Project</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Move project to archive. Can be restored later.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={<Archive size={16} />}
                  onClick={handleArchive}
                  disabled={isArchived || archiveMutation.isPending}
                >
                  {archiveMutation.isPending ? 'Archiving...' : 'Archive'}
                </Button>
              </Box>

              {/* Delete Project */}
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'error.main',
                  borderRadius: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography variant="subtitle2" color="error.main">
                    Delete Project
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Permanently delete project and all tasks.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<Trash2 size={16} />}
                  onClick={onDelete}
                >
                  Delete
                </Button>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Close
        </Button>
        <Button
          variant="contained"
          disabled={!hasChanges || updateMutation.isPending}
          onClick={handleSave}
          startIcon={<Save size={18} />}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectSettingsModal;
